<template>
  <div class="forum-admin-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-gear me-2"></i>Forum Administration
            </h4>
            <div class="d-flex gap-2">
              <button class="btn btn-primary" @click="showCreateModal()">
                <i class="bi bi-plus me-2"></i>New Forum
              </button>
            </div>
          </div>
          <div class="card-body">
            <!-- Tabs Navigation -->
            <ul class="nav nav-tabs mb-4">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'forums' }" @click="activeTab = 'forums'" href="#">
                  <i class="bi bi-chat-square-text me-2"></i>Forums
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'moderation' }" @click="activeTab = 'moderation'" href="#">
                  <i class="bi bi-shield-check me-2"></i>Moderation Queue
                </a>
              </li>
<!--              <li class="nav-item">-->
<!--                <a class="nav-link" :class="{ active: activeTab === 'analytics' }" @click="activeTab = 'analytics'" href="#">-->
<!--                  <i class="bi bi-bar-chart me-2"></i>Analytics-->
<!--                </a>-->
<!--              </li>-->
            </ul>
            <!-- Forums Tab -->
            <div v-if="activeTab === 'forums'" class="tab-content">
              <!-- Search and Filters -->
              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="input-group">
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Search forums..."
                      v-model="searchQuery"
                      @keyup.enter="applyFilters"
                    >
                    <button class="btn btn-outline-secondary" @click="applyFilters">
                      <i class="bi bi-search"></i>
                    </button>
                  </div>
                </div>
                <div class="col-md-3">
                  <select class="form-select" v-model="selectedWorkingGroup" @change="applyFilters">
                    <option value="">All Working Groups</option>
                    <option v-for="group in workingGroups" :key="group.id" :value="group.id">
                      {{ group.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-3">
                  <button class="btn btn-outline-primary w-100" @click="refreshData">
                    <i class="bi bi-arrow-clockwise me-2"></i>Refresh
                  </button>
                </div>
              </div>
              <!-- Loading State -->
              <div v-if="loading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <!-- Error State -->
              <div v-else-if="error" class="alert alert-danger">
                <i class="bi bi-exclamation-triangle me-2"></i>
                {{ error }}
                <button class="btn btn-sm btn-outline-danger ms-2" @click="refreshData">
                  Try Again
                </button>
              </div>
              <!-- Forums List -->
              <div v-else-if="forums.length > 0" class="table-responsive">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>Subject</th>
                    <th>Working Group</th>
                    <th>Creator</th>
                    <th>Topics</th>
                    <th>Moderation</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="forum in displayForums" :key="forum.id || forum._working_group_id">
                  <td>
                      <div>
                        <h6 class="mb-1">{{ forum.subject }}</h6>
                        <small class="text-muted">{{ truncateText(forum.description, 80) }}</small>
                      </div>
                    </td>
                    <td>
                      <span class="badge bg-secondary">{{ getWorkingGroupName(forum) }}</span>
                    </td>
                    <td>
                      <div class="d-flex align-items-center">
                        <img :src="getAvatarUrl(forum.creator)" alt="Avatar" class="rounded-circle me-2" style="width: 24px; height: 24px;">
                        <small>{{ getCreatorName(forum) }}</small>
                      </div>
                    </td>
                    <td class="text-center">
                      <span class="fw-bold">{{ forum.topics_count || forum.topic_count || 0 }}</span>
                    </td>
                    <td>
                      <span class="badge" :class="forum.enable_mod ? 'bg-warning' : 'bg-success'">
                        {{ forum.enable_mod ? 'Enabled' : 'Disabled' }}
                      </span>
                    </td>
                    <td>
                      <small>{{ formatDate(forum.created_at) }}</small>
                    </td>
                    <td>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                          <i class="bi bi-three-dots"></i>
                        </button>
                        <ul class="dropdown-menu">
                          <li><a class="dropdown-item" href="#" @click="viewForum(forum)">
                            <i class="bi bi-eye me-2"></i>View
                          </a></li>
                          <li><a class="dropdown-item" href="#" @click="editForum(forum)">
                            <i class="bi bi-pencil me-2"></i>Edit
                          </a></li>
                          <li><a class="dropdown-item" href="#" @click="toggleModeration(forum)">
                            <i class="bi bi-shield me-2"></i>{{ forum.enable_mod ? 'Disable' : 'Enable' }} Moderation
                          </a></li>
                          <li><hr class="dropdown-divider"></li>
                          <li><a class="dropdown-item text-danger" href="#" @click="deleteForum(forum)">
                            <i class="bi bi-trash me-2"></i>Delete
                          </a></li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="bi bi-chat-square-text text-muted" style="font-size: var(--font-size-4xl);"></i>
                <h5 class="mt-3 text-muted">No forums found</h5>
                <p class="text-muted">Create your first forum to get started</p>
                <button class="btn btn-primary" @click="showCreateModal()">
                  <i class="bi bi-plus me-2"></i>Create Forum
                </button>
              </div>
              <!-- Pagination -->
              <nav v-if="pagination && pagination.last_page > 1" class="mt-4">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                    <button class="page-link" @click="changePage(pagination.current_page - 1)" :disabled="pagination.current_page === 1">
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="page in getPageNumbers()"
                    :key="page"
                    class="page-item"
                    :class="{ active: pagination.current_page === page }"
                  >
                    <button class="page-link" @click="changePage(page)">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: pagination.current_page === pagination.last_page }">
                    <button class="page-link" @click="changePage(pagination.current_page + 1)" :disabled="pagination.current_page === pagination.last_page">
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
            <!-- Moderation Tab -->
            <div v-if="activeTab === 'moderation'" class="tab-content">
              <div v-if="loadingModeration && moderationQueue.length === 0" class="text-center py-4">
                <div class="spinner-border text-primary"></div>
              </div>
              <div v-else-if="!moderationQueue || moderationQueue.length === 0" class="text-center text-muted py-4">
              <i class="bi bi-inbox" style="font-size: var(--font-size-2xl);"></i>
                <p class="mt-2 mb-0">No comments awaiting moderation</p>
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover align-middle">
                  <thead>
                  <tr>
                    <th>Comment</th>
                    <th>Author</th>
                    <th>Forum Topic</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="comment in moderationQueue" :key="comment.id">
                    <td>{{ truncateText(parseCommentContent(comment.content), 100) }}</td>
                    <td>{{ comment.creator?.name || 'Unknown' }}</td>
                    <td>{{ comment.topic?.title || 'N/A' }}</td>
                    <td>
                      <div class="d-flex gap-2">
                        <button class="btn btn-sm btn-success" @click="approveComment(comment)">
                          <i class="bi bi-check-lg"></i> Approve
                        </button>
                        <button class="btn btn-sm btn-danger" @click="rejectComment(comment)">
                          <i class="bi bi-x-lg"></i> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <!-- Analytics Tab -->
            <div v-if="activeTab === 'analytics'" class="tab-content">
              <!-- Loading State for Analytics -->
              <div v-if="analyticsLoading" class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading analytics...</span>
                </div>
              </div>
              <!-- Analytics Cards -->
              <div v-else class="row mb-4">
                <div class="col-md-3">
                  <div class="card bg-primary text-white">
                    <div class="card-body text-center">
                      <h3 class="mb-1">{{ analytics.total_forums || 0 }}</h3>
                      <small>Total Forums</small>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-success text-white">
                    <div class="card-body text-center">
                      <h3 class="mb-1">{{ analytics.total_topics || 0 }}</h3>
                      <small>Total Topics</small>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-warning text-white">
                    <div class="card-body text-center">
                      <h3 class="mb-1">{{ analytics.total_comments || 0 }}</h3>
                      <small>Total Comments</small>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="card bg-info text-white">
                    <div class="card-body text-center">
                      <h3 class="mb-1">{{ analytics.active_working_groups || 0 }}</h3>
                      <small>Working Groups</small>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Working Groups Breakdown -->
              <div class="row">
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="mb-0">Forums by Working Group</h6>
                    </div>
                    <div class="card-body">
                      <div v-for="group in analytics.working_group_breakdown" :key="group.name" class="d-flex justify-content-between align-items-center mb-2">
                        <span>{{ group.name }}</span>
                        <span class="badge bg-secondary">{{ group.forum_count }} forums</span>
                      </div>
                      <div v-if="!analytics.working_group_breakdown || analytics.working_group_breakdown.length === 0" class="text-center text-muted py-3">
                        No data available
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="card">
                    <div class="card-header">
                      <h6 class="mb-0">Recent Activity</h6>
                    </div>
                    <div class="card-body">
                      <small class="text-muted">Today: {{ analytics.today_forums || 0 }} new forums</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Forum View Modal -->
    <div v-if="showViewModal" class="modal fade show" style="display: block; background-color: rgba(0,0,0,0.5);" @click.self="closeViewModal">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-chat-square-text me-2"></i>
              {{ viewingForum?.subject }}
            </h5>
            <button type="button" class="btn-close" @click="closeViewModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingForum" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading forum details...</span>
              </div>
            </div>
            <div v-else-if="viewingForum">
              <!-- Forum Details -->
              <div class="row mb-4">
                <div class="col-md-8">
                  <h6>Description</h6>
                  <p>{{ viewingForum.description }}</p>
                </div>
                <div class="col-md-4">
                  <h6>Details</h6>
                  <p><strong>Working Group:</strong> {{ getWorkingGroupName(viewingForum) }}</p>
                  <p><strong>Creator:</strong> {{ getCreatorName(viewingForum) }}</p>
                  <p><strong>Created:</strong> {{ formatDate(viewingForum.created_at) }}</p>
                  <p><strong>Moderation:</strong>
                    <span class="badge" :class="viewingForum.enable_mod ? 'bg-warning' : 'bg-success'">
                      {{ viewingForum.enable_mod ? 'Enabled' : 'Disabled' }}
                    </span>
                  </p>
                </div>
              </div>
              <!-- Topics Section -->
              <div class="border-top pt-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h6 class="mb-0">
                    <i class="bi bi-list-task me-2"></i>
                    Topics ({{ forumTopics.length }})
                  </h6>
                  <button class="btn btn-sm btn-primary" @click="showCreateTopicModal = true">
                    <i class="bi bi-plus me-1"></i>New Topic
                  </button>
                </div>
                <div v-if="loadingTopics" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                </div>
                <div v-else-if="forumTopics.length > 0" class="list-group">
                  <div v-for="topic in forumTopics" :key="topic.id" class="list-group-item">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="flex-grow-1">
                        <h6 class="mb-1">{{ topic.title }}</h6>
                        <p class="mb-1 text-muted small">{{ truncateText(topic.content, 100) }}</p>
                        <small class="text-muted">
                          By {{ topic.creator?.name || 'Unknown' }} •
                          {{ formatDate(topic.created_at) }} •
                          {{ topic.comments_count || 0 }} comments •
                          {{ topic.views || 0 }} views
                        </small>
                      </div>
                      <div class="d-flex gap-1">
                        <span v-if="topic.is_sticky" class="badge bg-warning text-dark">
                          <i class="bi bi-pin-fill"></i> Sticky
                        </span>
                        <span v-if="topic.is_locked" class="badge bg-danger">
                          <i class="bi bi-lock-fill"></i> Locked
                        </span>
                        <span class="badge" :class="getPriorityBadgeClass(topic.priority)">
                          {{ topic.priority }}
                        </span>
                      </div>
                    </div>
                    <!-- Comments Preview -->
                    <div v-if="topic.recent_comments && topic.recent_comments.length > 0" class="mt-2 border-top pt-2">
                      <small class="text-muted fw-bold">Recent Comments:</small>
                      <div v-for="comment in topic.recent_comments.slice(0, 2)" :key="comment.id" class="ms-3 mt-1">
                        <div class="d-flex align-items-start">
                          <img :src="getAvatarUrl(comment.creator)" alt="Avatar" class="rounded-circle me-2" style="width: 20px; height: 20px;">
                          <div class="flex-grow-1">
                            <small class="fw-bold">{{ comment.creator?.name || 'Unknown' }}</small>
                            <small class="text-muted ms-2">{{ formatDate(comment.created_at) }}</small>
                            <p class="mb-0 small">{{ truncateText(comment.content, 80) }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else class="text-center py-4 text-muted">
                  <i class="bi bi-chat-dots" style="font-size: var(--font-size-2xl);"></i>
                  <p class="mb-0 mt-2">No topics yet. Be the first to start a discussion!</p>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Create/Edit Forum Modal -->
    <div class="modal fade" :class="{ show: showCreateForumModal, 'd-block': showCreateForumModal }" v-if="showCreateForumModal" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingForum ? 'Edit Forum' : 'Create New Forum' }}</h5>
            <button type="button" class="btn-close" @click="closeForumModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveForum">
              <div class="mb-3">
                <label class="form-label">Working Group *</label>
                <select
                  class="form-select"
                  v-model="forumForm.working_group_id"
                  :class="{ 'is-invalid': !forumForm.working_group_id }"
                  required
                >
                  <option value="">Select Working Group</option>
                  <option v-for="group in workingGroups" :key="group.id" :value="group.id">
                    {{ group.name }}
                  </option>
                </select>
                <div v-if="!forumForm.working_group_id && forumForm.working_group_id !== null" class="invalid-feedback">
                  Please select a working group
                </div>
                <div v-if="workingGroups.length === 0" class="form-text text-warning">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  No working groups available. Please contact an administrator.
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Subject *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="forumForm.subject"
                  :class="{ 'is-invalid': forumForm.subject && forumForm.subject.trim().length < 3 }"
                  required
                  maxlength="255"
                  placeholder="Enter forum subject (minimum 3 characters)"
                >
                <div class="d-flex justify-content-between">
                  <div v-if="forumForm.subject && forumForm.subject.trim().length < 3" class="invalid-feedback d-block">
                    Subject must be at least 3 characters long
                  </div>
                  <small class="form-text text-muted ms-auto">{{ forumForm.subject.length }}/255 characters</small>
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Description *</label>
                <textarea
                  class="form-control"
                  rows="4"
                  v-model="forumForm.description"
                  :class="{ 'is-invalid': forumForm.description && forumForm.description.trim().length < 10 }"
                  required
                  placeholder="Enter forum description (minimum 10 characters)"
                ></textarea>
                <div v-if="forumForm.description && forumForm.description.trim().length < 10" class="invalid-feedback">
                  Description must be at least 10 characters long
                </div>
                <small class="form-text text-muted">{{ forumForm.description.length }} characters</small>
              </div>
              <div class="mb-3">
                <div class="form-check">
                  <input class="form-check-input" type="checkbox" v-model="forumForm.enable_mod" id="enableMod">
                  <label class="form-check-label" for="enableMod">
                    Enable moderation for this forum
                  </label>
                  <small class="form-text text-muted d-block">Posts will require approval before being visible</small>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeForumModal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveForum"
              :disabled="saving || !isFormValid"
            >
              <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ editingForum ? 'Update' : 'Create' }} Forum
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Create Topic Modal -->
    <div v-if="showCreateTopicModal" class="modal fade show" style="display: block; background-color: rgba(0,0,0,0.5);" data-bs-backdrop="static" data-bs-keyboard="false">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-plus-circle me-2"></i>
              Create New Topic
            </h5>
            <button type="button" class="btn-close" @click="closeTopicModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTopic">
              <div class="mb-3">
                <label class="form-label">Title *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="topicForm.title"
                  required
                  maxlength="255"
                  placeholder="Enter topic title"
                >
                <small class="form-text text-muted">{{ topicForm.title.length }}/255 characters</small>
              </div>
              <div class="mb-3">
                <label class="form-label">Content *</label>
                <textarea
                  class="form-control"
                  rows="6"
                  v-model="topicForm.content"
                  required
                  placeholder="Enter topic content"
                ></textarea>
                <small class="form-text text-muted">Minimum 10 characters required</small>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <select class="form-select" v-model="topicForm.priority">
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Options</label>
                    <div>
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="topicForm.is_sticky" id="topicSticky">
                        <label class="form-check-label" for="topicSticky">
                          Sticky Topic
                        </label>
                      </div>
                      <div class="form-check">
                        <input class="form-check-input" type="checkbox" v-model="topicForm.is_locked" id="topicLocked">
                        <label class="form-check-label" for="topicLocked">
                          Lock Topic
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeTopicModal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              @click="saveTopic"
              :disabled="savingTopic || !isTopicFormValid"
            >
              <span v-if="savingTopic" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Create Topic
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-exclamation-triangle text-warning me-2"></i>
              Confirm Delete
            </h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p class="mb-0">
              Are you sure you want to delete the forum "<strong>{{ forumToDelete?.subject }}</strong>"?
              This action cannot be undone and will also delete all associated topics and comments.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Cancel</button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmDelete"
              :disabled="deleting"
            >
              <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              <i class="bi bi-trash me-2"></i>Delete
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ForumService from '@/services/forums/ForumServices'
import { avatarMixin } from '@/mixins/avatarMixin'

export default {
  name: 'ForumAdmin',
  mixins: [avatarMixin],
  data() {
    return {
      activeTab: 'forums',
      loading: false,
      saving: false,
      deleting: false,
      savingTopic: false,
      analyticsLoading: false,
      loadingForum: false,
      loadingTopics: false,
      error: null,
      // Forum Management
      forums: [],
      workingGroups: [],
      pagination: null,
      searchQuery: '',
      selectedWorkingGroup: '',
      currentPage: 1,
      // Forum Viewing
      viewingForum: null,
      forumTopics: [],
      showViewModal: false,
      showCreateTopicModal: false,
      // Modals
      showCreateForumModal: false,
      showDeleteModal: false,
      editingForum: null,
      forumToDelete: null,
      // Form
      forumForm: {
        subject: '',
        description: '',
        working_group_id: '',
        enable_mod: false
      },
      // Topic Form
      topicForm: {
        title: '',
        content: '',
        priority: 'low',
        is_sticky: false,
        is_locked: false
      },
      // Moderation Queue
      moderationQueue: [],
      // Analytics
      analytics: {
        total_forums: 0,
        total_topics: 0,
        total_comments: 0,
        today_forums: 0,
        active_working_groups: 0,
        working_group_breakdown: []
      },
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
    }
  },
  computed: {
    displayForums() {
      return this.forums.map(row => {
        // Normal forum (already correct)
        if (row.id) return row;

        // Working-group wrapped forum
        if (row.forum) {
          return {
            ...row.forum,
            working_group: row.working_group,
            working_group_id: row.working_group?.id
          };
        }

        // Placeholder forum (not created yet)
        return {
          id: null,
          subject: row.working_group.name + ' Forum',
          description: 'Discussion space for ' + row.working_group.name,
          working_group: row.working_group,
          working_group_id: row.working_group.id,
          enable_mod: false,
          _placeholder: true
        };
      });
    },
    isTopicFormValid() {
      return (
        this.topicForm.title &&
        this.topicForm.title.trim().length >= 3 &&
        this.topicForm.content &&
        this.topicForm.content.trim().length >= 10
      );
    },
    isFormValid() {
      return (
        this.forumForm.subject &&
        this.forumForm.subject.trim().length >= 3 &&
        this.forumForm.description &&
        this.forumForm.description.trim().length >= 10 &&
        this.forumForm.working_group_id
      );
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'moderation') {
        this.loadModerationQueue()
      }
    }
  },
  async mounted() {
    await this.initializeData();
  },
  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    async initializeData() {
      await this.loadWorkingGroups();
      await this.loadForums();
      await this.loadAnalytics();
    },
    parseCommentContent(content) {
      try {
        const parsed = JSON.parse(content)
        // Handle structure like { content: "text" }
        return parsed?.content || content
      } catch {
        return content
      }
    },
    async loadForums(page = 1) {
      this.loading = true;
      this.error = null;

      const requestedPage = page;

      try {
        const params = {
          page: requestedPage,
          per_page: 15
        };

        if (this.selectedWorkingGroup) {
          params.working_group_id = this.selectedWorkingGroup;
        }
        if (this.searchQuery.trim()) {
          params.search = this.searchQuery.trim();
        }

        const response = await ForumService.getForums(params);

        if (response?.success) {
          const data = response.data;

          // Handle paginated response
          if (data && typeof data === 'object' && 'data' in data) {
            this.forums = Array.isArray(data.data) ? data.data : [];

            // FIXED: Ensure current_page matches what we requested
            this.pagination = {
              last_page: data.last_page || 1,
              per_page: data.per_page || 15,
              total: data.total || 0,
              current_page: data.current_page || requestedPage // Use server response or fallback
            };

            // Update local currentPage to match pagination
            this.currentPage = this.pagination.current_page;

          } else if (Array.isArray(data)) {
            // Direct array response (no pagination)
            this.forums = data;
            this.pagination = {
              current_page: 1,
              last_page: 1,
              per_page: data.length,
              total: data.length
            };
            this.currentPage = 1;
          } else {
            this.forums = [];
            this.pagination = null;
            this.currentPage = 1;
          }

        } else {
          this.error = response?.message || 'Failed to load forums';
          this.forums = [];
          console.error('API Error:', response); // Debug log
        }
      } catch (error) {
        console.error('Error loading forums:', error);
        this.error = 'Failed to load forums: ' + error.message;
        this.forums = [];
      } finally {
        this.loading = false;
      }
    },
    async loadWorkingGroups() {
      try {
        const res = await ForumService.getWorkingGroups();

        const items =
          Array.isArray(res?.data) ? res.data :
            Array.isArray(res?.data?.data) ? res.data.data :
              Array.isArray(res?.data?.results) ? res.data.results :
                [];

        this.workingGroups = items
          .filter(g => g && (g.id ?? g.uuid) && (g.name ?? g.title))
          .map(g => ({
            id: g.id ?? g.uuid,
            name: g.name ?? g.title
          }));

        if (!items.length) {
          console.warn('Unexpected working-groups response shape:', res);
        }
      } catch (err) {
        console.error('Error loading working groups:', err);
        this.workingGroups = [];
      }
    },
    async loadAnalytics() {
      this.analyticsLoading = true;
      try {
        const response = await ForumService.getAnalytics();
        if (response?.success) {
          const data = response.data;
          this.analytics = {
            total_forums: data?.total_forums || 0,
            total_topics: data?.total_topics || 0,
            total_comments: data?.total_comments || 0,
            today_forums: data?.today_forums || 0,
            active_working_groups: data?.active_working_groups || 0,
            working_group_breakdown: Array.isArray(data?.working_group_breakdown) ?
              data.working_group_breakdown : []
          };
        }
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        this.analyticsLoading = false;
      }
    },
    async loadModerationQueue() {
      this.loadingModeration = true;

      try {
        const response = await ForumService.getPendingComments();

        let extracted = [];

        // handle { success: true, data: { success: true, data: [...] } }
        if (Array.isArray(response?.data)) {
          extracted = response.data;
        } else if (Array.isArray(response?.data?.data)) {
          extracted = response.data.data;
        } else if (Array.isArray(response?.data?.data?.data)) {
          extracted = response.data.data.data;
        } else if (response?.success && Array.isArray(response?.Data?.data)) {
          extracted = response.Data.data;
        } else {
          console.warn('⚠️ Could not find array in response, defaulting to empty array.');
        }

        this.moderationQueue = extracted;
      } catch (error) {
        console.error('❌ loadModerationQueue() error:', error);
        this.moderationQueue = [];
      } finally {
        this.loadingModeration = false;
      }
    },

    async approveComment(comment) {
      try {
        // Optimistic UI: remove immediately so the user sees instant feedback
        const previousQueue = [...this.moderationQueue];
        this.moderationQueue = this.moderationQueue.filter(c => c.id !== comment.id);

        const response = await ForumService.approveComment(comment.id);

        if (response?.success) {
          this.showToast('success','Comment approved successfully');

          // Ensure we reload the moderation queue from the server
          // (in case server-side filtering/pagination changed)
          await this.loadModerationQueue();
        } else {
          // Revert optimistic change if server returned failure
          this.moderationQueue = previousQueue;
          this.showToast('error',response?.message || 'Failed to approve comment');
        }
      } catch (error) {
        console.error('Error approving comment:', error);
        // Revert optimistic change on exception
        this.moderationQueue = this.moderationQueue.filter(c => c.id !== comment.id) ? this.moderationQueue : this.moderationQueue;
        this.showToast('error',error.message || 'Failed to approve comment');
        // Try to reload queue to get consistent state
        await this.loadModerationQueue();
      }
    },

    async rejectComment(comment) {
      try {
        // Optimistic UI
        const previousQueue = [...this.moderationQueue];
        this.moderationQueue = this.moderationQueue.filter(c => c.id !== comment.id);

        const response = await ForumService.rejectComment(comment.id);

        if (response?.success) {
          this.showToast('success','Comment rejected and user notified');

          // Refresh list from server
          await this.loadModerationQueue();
        } else {
          // Revert optimistic change
          this.moderationQueue = previousQueue;
          this.showToast('error',response?.message || 'Failed to reject comment');
        }
      } catch (error) {
        console.error('Error rejecting comment:', error);
        // Revert optimistic change on exception
        this.moderationQueue = previousQueue;
        this.showToast('error',error.message || 'Failed to reject comment');
        await this.loadModerationQueue();
      }
    },

    async loadForumDetails(forum) {
      this.loadingForum = true;
      this.loadingTopics = true;
      try {
        const response = await ForumService.getForum(forum.id);
        if (response?.success) {
          // FIX: your forum is nested in response.data.data
          const forumData = response.data?.data || response.data;

          this.viewingForum = JSON.parse(JSON.stringify(forumData));

          // Load topics if present
          if (this.viewingForum.topics) {
            this.forumTopics = Array.isArray(this.viewingForum.topics)
              ? this.viewingForum.topics
              : [];
          } else {
            await this.loadForumTopics(forum.id);
          }
        } else {
          this.showToast('error',response?.message || 'Failed to load forum details');
        }
      } catch (error) {
        console.error('Error loading forum details:', error);
        this.showToast('error','Failed to load forum details');
      } finally {
        this.loadingForum = false;
        this.loadingTopics = false;
      }
    },
    async loadForumTopics(forumId) {
      try {
        const response = await ForumService.getForumTopics(forumId);
        if (response?.success) {
          const data = response.data;
          // Handle paginated response
          if (data && typeof data === 'object' && 'data' in data) {
            this.forumTopics = Array.isArray(data.data) ? data.data : [];
          } else if (Array.isArray(data)) {
            this.forumTopics = data;
          } else {
            this.forumTopics = [];
          }
        } else {
          this.forumTopics = [];
        }
      } catch (error) {
        console.error('Error loading forum topics:', error);
        this.forumTopics = [];
      }
    },
    // Topic management
    async saveTopic() {
      if (!this.isTopicFormValid || !this.viewingForum) {
        console.error('Topic form validation failed');
        return;
      }

      // FIXED: Send individual fields, not the entire form object
      const topicData = {
        title: this.topicForm.title.trim(),
        content: this.topicForm.content.trim(),
        priority: this.topicForm.priority,
        is_sticky: Boolean(this.topicForm.is_sticky),
        is_locked: Boolean(this.topicForm.is_locked)
      };

      this.savingTopic = true;
      try {
        const response = await ForumService.createTopic(this.viewingForum.id, topicData);

        if (response?.success) {
          this.showToast('success','Topic created successfully!');
          this.closeTopicModal();
          await this.loadForumDetails(this.viewingForum);
        } else {
          console.error('Topic creation failed:', response);
          this.showToast('error',response?.message || 'Failed to create topic');
        }
      } catch (error) {
        console.error('Topic creation exception:', error);
        this.showToast('error','Failed to create topic');
      } finally {
        this.savingTopic = false;
      }
    },

    closeTopicModal() {
      this.showCreateTopicModal = false;
      this.topicForm = {
        title: '',
        content: '',
        priority: 'low',
        is_sticky: false,
        is_locked: false
      };
    },
    // Filter and pagination methods
    async applyFilters() {
      this.currentPage = 1;
      await this.loadForums(1);
    },
    async refreshData() {
      const currentPageToLoad = this.currentPage || 1;
      await this.loadWorkingGroups();
      await this.loadForums(currentPageToLoad);
      await this.loadAnalytics();
    },
    async changePage(page) {
      if (page >= 1 && page <= (this.pagination?.last_page || 1) && page !== this.currentPage) {
        this.currentPage = page;
        await this.loadForums(page);
      }
    },
    getPageNumbers() {
      if (!this.pagination) return [];
      const pages = [];
      const current = this.pagination.current_page;
      const last = this.pagination.last_page;
      let start = Math.max(1, current - 2);
      let end = Math.min(last, start + 4);
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    },
    // Forum actions
    async viewForum(forum) {
      // If forum not created yet → create it first
      if (!forum.id && forum._working_group_id) {
        const res = await ForumService.createForum({
          subject: forum.subject,
          description: forum.description,
          working_group_id: forum._working_group_id,
          enable_mod: false
        });

        if (!res?.success) {
          this.showToast('error','Failed to create forum');
          return;
        }

        await this.loadForums(this.currentPage);
        return;
      }

      this.viewingForum = forum;
      this.showViewModal = true;
      await this.loadForumDetails(forum);
    },
    closeViewModal() {
      this.showViewModal = false;
      this.viewingForum = null;
      this.forumTopics = [];
    },
    editForum(forum) {
      if (!forum.id) {
        this.showToast('error', 'Forum does not exist yet. Please create it first.');
        return;
      }

      this.editingForum = forum;

      this.forumForm = {
        subject: forum.subject,
        description: forum.description,
        working_group_id: forum.working_group_id || forum.working_group?.id,
        enable_mod: forum.enable_mod
      };

      this.showCreateForumModal = true;
    },
    async toggleModeration(forum) {
      try {
        const response = await ForumService.updateForum(forum.id, {
          subject: forum.subject,
          description: forum.description,
          working_group_id: forum.working_group_id || forum.working_group?.id,
          enable_mod: !forum.enable_mod
        });
        if (response?.success) {
          forum.enable_mod = !forum.enable_mod;
          this.showToast('success',`Moderation ${forum.enable_mod ? 'enabled' : 'disabled'} for forum`);
        } else {
          this.showToast('error',response?.message || 'Failed to update moderation setting');
        }
      } catch (error) {
        console.error('Error toggling moderation:', error);
        this.showToast('error','Failed to update moderation setting');
      }
    },
    deleteForum(forum) {
      this.forumToDelete = forum;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      if (!this.forumToDelete) return;
      this.deleting = true;
      try {
        const response = await ForumService.deleteForum(this.forumToDelete.id);
        if (response?.success) {
          this.showToast('success','Forum deleted successfully!');
          await this.loadForums(this.currentPage);
          await this.loadAnalytics();
        } else {
          this.showToast('error',response?.message || 'Failed to delete forum');
        }
      } catch (error) {
        console.error('Error deleting forum:', error);
        this.showToast('error','Failed to delete forum');
      } finally {
        this.deleting = false;
        this.showDeleteModal = false;
        this.forumToDelete = null;
      }
    },
    async showCreateModal() {
      // Make sure working groups are loaded
      if (this.workingGroups.length === 0) {
        await this.loadWorkingGroups();
      }
      if (this.workingGroups.length === 0) {
        this.showToast('error','No working groups available. Please contact an administrator.');
        return;
      }
      this.showCreateForumModal = true;
    },
    async saveForum() {
      if (!this.isFormValid) {
        console.error('Form validation failed locally');
        this.showToast('error','Please fill in all required fields correctly');
        return;
      }

      // Prepare data exactly as we want to send it
      const formData = {
        subject: this.forumForm.subject?.toString().trim() || '',
        description: this.forumForm.description?.toString().trim() || '',
        working_group_id: this.forumForm.working_group_id,
        enable_mod: Boolean(this.forumForm.enable_mod)
      };

      // Final validation
      if (!formData.subject || formData.subject.length < 3) {
        console.error('Subject validation failed:', formData.subject);
        this.showToast('error','Forum subject must be at least 3 characters long');
        return;
      }

      if (!formData.description || formData.description.length < 10) {
        console.error('Description validation failed:', formData.description);
        this.showToast('error','Forum description must be at least 10 characters long');
        return;
      }

      if (!formData.working_group_id) {
        this.showToast('error','Please select a working group');
        return;
      }

      this.saving = true;
      try {
        let response;

        if (this.editingForum) {
          response = await ForumService.updateForum(this.editingForum.id, formData);
        } else {
          response = await ForumService.createForum(formData);
        }

        if (response?.success) {
          this.showToast('success',`Forum ${this.editingForum ? 'updated' : 'created'} successfully!`);
          this.closeForumModal();
          await this.loadForums(this.currentPage);
          await this.loadAnalytics();
        } else {
          console.error('Server response indicates failure:', response);

          // Handle validation errors from server
          if (response?.errors && typeof response.errors === 'object') {
            const errorMessages = [];
            Object.keys(response.errors).forEach(field => {
              if (Array.isArray(response.errors[field])) {
                errorMessages.push(...response.errors[field]);
              }
            });
            console.error('Validation errors:', errorMessages);
            this.showToast('error',errorMessages.join(', ') || response?.message || 'Validation failed');
          } else {
            this.showToast('error',response?.message || `Failed to ${this.editingForum ? 'update' : 'create'} forum`);
          }
        }
      } catch (error) {
        console.error('Exception caught:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          request: error.request
        });

        // Handle validation errors from server response
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          const errorMessages = [];
          Object.keys(errors).forEach(field => {
            if (Array.isArray(errors[field])) {
              errorMessages.push(...errors[field]);
            }
          });
          console.error('Server validation errors:', errorMessages);
          this.showToast('error',errorMessages.join(', '));
        } else {
          this.showToast('error',error.response?.data?.message || error.message || `Failed to ${this.editingForum ? 'update' : 'create'} forum`);
        }
      } finally {
        this.saving = false;
      }

    },
    closeForumModal() {
      this.showCreateForumModal = false;
      this.editingForum = null;
      this.forumForm = {
        subject: '',
        description: '',
        working_group_id: '',
        enable_mod: false
      };
    },
    // Utility methods
    getWorkingGroupName(forum) {
      if (!forum) return 'No Group';
      return forum.working_group?.name ||
        forum.workingGroup?.name ||
        'No Group';
    },
    getCreatorName(forum) {
      if (!forum) return 'Unknown';
      return forum.creator?.name || 'Unknown';
    },
    getAvatarUrl(user) {
      return this.getUserAvatar(user);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString();
      } catch (error) {
        return 'Invalid Date';
      }
    },
    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    getPriorityBadgeClass(priority) {
      switch (priority) {
        case 'high': return 'bg-danger';
        case 'medium': return 'bg-warning';
        case 'low':
        default: return 'bg-secondary';
      }
    }
  }
}
</script>

<style scoped>
.forum-admin-page {
  padding: 1rem 0;
}
.nav-tabs .nav-link {
  cursor: pointer;
}
.nav-tabs .nav-link.active {
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
}
.tab-content {
  min-height: 400px;
}
.card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}
.dropdown-toggle::after {
  display: none;
}
.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: var(--font-size-sm);
}
.dropdown-item:hover {
  background-color: #f8f9fa;
}
.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}
.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
.list-group-item {
  border-left: 4px solid transparent;
  transition: border-color 0.2s ease;
}
.list-group-item:hover {
  border-left-color: #009CAD;
}
</style>
